import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { novoRegistroBase } from '../../db/tipos'
import type { ClienteRepresentada } from '../../db/tipos'
import { gravarLocalEEnfileirar } from '../../sync/outbox'
import { confirmar } from '../../componentes/confirmar'

const estiloCampo = 'w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white'

function validade(fim: string | null): string | null {
  if (!fim) return null
  const [a, m, d] = fim.slice(0, 10).split('-')
  return d && m && a ? `Válida até ${d}/${m}/${a}` : null
}

// Pedido dela 10/10: a aba Comercial mostra a TABELA DE PRECO especifica deste
// cliente, uma por fornecedora. "+" escolhe entre as tabelas do sistema. A
// escolha mora em `clientes_representadas.tabela_preco_padrao_id`, o mesmo campo
// que o pedido ja usa para colocar essa tabela na frente na busca de produtos.
export default function TabelasDoCliente({ clienteId }: { clienteId: string }) {
  const representadas = useLiveQuery(
    () => banco.representadas.filter((r) => r.apagado_em === null).sortBy('nome'),
    [],
  )
  const tabelas = useLiveQuery(
    () => banco.tabelas_preco.filter((t) => t.apagado_em === null).sortBy('nome'),
    [],
  )
  const vinculos = useLiveQuery(
    () =>
      banco.clientes_representadas
        .where('cliente_id').equals(clienteId)
        .filter((v) => v.apagado_em === null)
        .toArray(),
    [clienteId],
  )

  const [adicionando, setAdicionando] = useState(false)
  const [marcaNova, setMarcaNova] = useState('')
  const [tabelaNova, setTabelaNova] = useState('')
  const [editando, setEditando] = useState<string | null>(null)
  const [tabelaEditada, setTabelaEditada] = useState('')

  async function gravarTabela(representadaId: string, tabelaId: string | null) {
    const existente = vinculos?.find((v) => v.representada_id === representadaId)
    if (existente) {
      // Remover (tabelaId null) SO solta a fixacao: a tabela fica como a
      // preferencia aprendida -- passa na frente no pedido, sem esconder as
      // outras -- ate um pedido ensinar outra. Antes, remover zerava a
      // preferencia tambem (code review 11/10, ponto 8).
      if (tabelaId === null) {
        if (!existente.tabela_preco_fixa) return
        await gravarLocalEEnfileirar('clientes_representadas', { ...existente, tabela_preco_fixa: false })
        return
      }
      if (existente.tabela_preco_padrao_id === tabelaId && existente.tabela_preco_fixa) return
      // Escolhida aqui = FIXA (10/10): o pedido nao regrava e mostra so ela.
      await gravarLocalEEnfileirar('clientes_representadas', {
        ...existente, tabela_preco_padrao_id: tabelaId, tabela_preco_fixa: true,
      })
    } else if (tabelaId !== null) {
      const novo: ClienteRepresentada = {
        ...novoRegistroBase(),
        cliente_id: clienteId,
        representada_id: representadaId,
        codigo_cliente_na_representada: null,
        tabela_preco_padrao_id: tabelaId,
        tabela_preco_fixa: true,
        observacoes: null,
      }
      await gravarLocalEEnfileirar('clientes_representadas', novo)
    }
  }

  const cabecalho = (
    <div>
      <h3 className="font-bold text-slate-800">Tabela de preço</h3>
      <p className="text-sm text-slate-500">Tabela específica deste cliente em cada fornecedora. No pedido, só ela aparece daquela fornecedora.</p>
    </div>
  )

  // Nao desenhar antes de carregar: lista vazia por engano convidaria a
  // "adicionar" uma tabela que ja esta escolhida.
  if (vinculos === undefined || representadas === undefined || tabelas === undefined) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
        {cabecalho}
        <p className="text-xs text-slate-500">Carregando...</p>
      </div>
    )
  }

  const tabelasAtivasDe = (representadaId: string) =>
    tabelas.filter((t) => t.representada_id === representadaId && t.ativa)
  const escolhidas = vinculos
    // So as ESCOLHIDAS a mao: a tabela que o pedido apenas aprendeu nao e
    // "especifica do cliente" e nao aparece aqui.
    .filter((v) => v.tabela_preco_padrao_id !== null && v.tabela_preco_fixa === true)
    .map((v) => ({
      vinculo: v,
      marca: representadas.find((r) => r.id === v.representada_id)?.nome ?? '',
      tabela: tabelas.find((t) => t.id === v.tabela_preco_padrao_id),
    }))
    .sort((a, b) => a.marca.localeCompare(b.marca, 'pt-BR'))
  const jaTem = new Set(escolhidas.map((l) => l.vinculo.representada_id))
  const marcasLivres = representadas.filter((r) => !jaTem.has(r.id) && tabelasAtivasDe(r.id).length > 0)

  function abrirAdicao() {
    const primeira = marcasLivres[0]
    setAdicionando(true)
    setMarcaNova(primeira?.id ?? '')
    setTabelaNova(primeira ? tabelasAtivasDe(primeira.id)[0]?.id ?? '' : '')
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 space-y-3" data-testid="tabelas-do-cliente">
      <div className="flex items-start justify-between gap-3">
        {cabecalho}
        <button
          type="button"
          aria-label="Adicionar tabela"
          disabled={marcasLivres.length === 0}
          onClick={abrirAdicao}
          className="shrink-0 w-9 h-9 rounded-lg border border-teal-700 text-teal-700 text-xl leading-none disabled:opacity-40"
        >
          +
        </button>
      </div>

      {adicionando && (
        <div className="bg-teal-50 rounded-lg p-3 space-y-2">
          <select
            aria-label="Fornecedora da tabela"
            value={marcaNova}
            onChange={(e) => {
              setMarcaNova(e.target.value)
              setTabelaNova(tabelasAtivasDe(e.target.value)[0]?.id ?? '')
            }}
            className={estiloCampo}
          >
            {marcasLivres.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
          </select>
          <select
            aria-label="Tabela de preço do cliente"
            value={tabelaNova}
            onChange={(e) => setTabelaNova(e.target.value)}
            className={estiloCampo}
          >
            {tabelasAtivasDe(marcaNova).map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
          <div className="flex gap-2">
            <button type="button" onClick={() => setAdicionando(false)}
              className="flex-1 border border-slate-300 text-slate-600 rounded-lg py-1.5 text-sm bg-white">
              Cancelar
            </button>
            <button
              type="button"
              disabled={!marcaNova || !tabelaNova}
              onClick={() => void gravarTabela(marcaNova, tabelaNova).then(() => setAdicionando(false))}
              className="flex-1 bg-teal-700 text-white rounded-lg py-1.5 text-sm disabled:opacity-40"
            >
              Adicionar
            </button>
          </div>
        </div>
      )}

      {escolhidas.length === 0 && !adicionando && (
        <p className="text-sm text-slate-500">Nenhuma tabela específica. Toque no + para escolher.</p>
      )}

      {escolhidas.map(({ vinculo, marca, tabela }) => {
        const emEdicao = editando === vinculo.representada_id
        return (
          <div key={vinculo.id} className="border-t border-slate-100 pt-2 space-y-2" data-testid="linha-tabela">
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 break-words">{marca}</p>
                <p className="text-sm text-slate-600 break-words">
                  {tabela ? tabela.nome : 'Tabela removida'}
                  {tabela && !tabela.ativa && ' (inativa)'}
                </p>
                {tabela && validade(tabela.vigencia_fim) && (
                  <p className="text-xs text-slate-500">{validade(tabela.vigencia_fim)}</p>
                )}
              </div>
              {!emEdicao && (
                <>
                  <button type="button" aria-label={`Trocar tabela de ${marca}`}
                    onClick={() => {
                      // Tabela atual inativa/apagada nao esta entre as opcoes: o
                      // select mostraria a 1a ativa e o estado guardaria a velha,
                      // e o ✓ nao trocaria nada (code review 11/10).
                      const ativas = tabelasAtivasDe(vinculo.representada_id)
                      const atual = vinculo.tabela_preco_padrao_id
                      setEditando(vinculo.representada_id)
                      setTabelaEditada(ativas.some((t) => t.id === atual) ? atual! : (ativas[0]?.id ?? ''))
                    }}
                    className="p-1.5 text-teal-700">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4" /></svg>
                  </button>
                  <button type="button" aria-label={`Remover tabela de ${marca}`}
                    onClick={() => {
                      void confirmar(`Remover a tabela específica de ${marca}?`, { botao: 'Remover', perigo: true }).then((sim) => {
                        if (sim) void gravarTabela(vinculo.representada_id, null)
                      })
                    }}
                    className="p-1.5 text-red-600">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6" /></svg>
                  </button>
                </>
              )}
            </div>
            {emEdicao && (
              <div className="flex gap-2 items-center">
                <select
                  aria-label={`Nova tabela para ${marca}`}
                  value={tabelaEditada}
                  onChange={(e) => setTabelaEditada(e.target.value)}
                  className={estiloCampo}
                >
                  {tabelasAtivasDe(vinculo.representada_id).map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </select>
                <button type="button" aria-label={`Salvar tabela de ${marca}`}
                  disabled={!tabelaEditada}
                  onClick={() => void gravarTabela(vinculo.representada_id, tabelaEditada).then(() => setEditando(null))}
                  className="px-2 py-1 rounded-lg bg-teal-700 text-white text-sm disabled:opacity-40">
                  ✓
                </button>
                <button type="button" aria-label="Cancelar troca" onClick={() => setEditando(null)}
                  className="px-2 py-1 rounded-lg border border-slate-300 text-slate-600 text-sm">
                  ✕
                </button>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
